/**
 * The suspense read behind an instance mount: a structure module and the
 * instance's active feature toggles, fetched in parallel.
 *
 * `useInstanceConfig` takes the instance's identity, its feature source, and
 * its module loader; builds both queries; runs them through one
 * `useSuspenseQueries` call, which starts both fetches before suspending on
 * either; and assembles the mounted instance's config from the results. The
 * reading locale is injected here rather than named by each route layout,
 * the same way `useSiloQuery` injects it, so a features read shares the cache
 * entry the rest of the app reads that resource under.
 *
 * Only a space and a story have toggles of their own, and each names the
 * resource that holds them: a space the space resource, a story the stories
 * resource. Every other structure omits the source and takes its features
 * from the structure and deployment defaults `assembleInstanceConfig` reads.
 */
import { useSuspenseQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useApiQueryUtils } from 'system/api-client-context';
import { useSiloLanguage } from 'system/request-metadata';
import { assembleInstanceConfig, type FeaturesQueryOptions, type FeaturesSource, type InstanceConfig } from 'util/features';
import { EMPTY_OBJECT } from 'util/stdtypes';
import { type StructureModule, structureModuleQueryOptions } from 'util/structure-module';

/**
 * Stands in for the features query when a mount names no source, so the pair
 * of queries below is always a pair and the module read keeps its slot.
 *
 * `initialData` is what makes it resolved rather than fetched: a suspense query
 * holding data already never suspends, so a mount with no features to read
 * waits on its structure module alone.
 */
const RESOLVED_EMPTY_FEATURES: FeaturesQueryOptions = {
    queryKey: ['noInstanceFeatures'],
    queryFn: () => EMPTY_OBJECT as Record<string, boolean>,
    initialData: EMPTY_OBJECT as Record<string, boolean>,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
};

/**
 * Resolves everything an instance mount needs: the structure module, the
 * instance's active feature toggles, and the `config`/`screenSet` pair
 * assembled from both. The two queries fetch in parallel, and the caller
 * suspends until both are ready. Every identity resolves: an instance that
 * does not exist, or is hidden from the caller, gets the default
 * configuration, since its feature map reads as empty. A failed read throws.
 *
 * `featuresSource` is omitted by a structure with no per-instance toggles,
 * which is every structure but a space and a story.
 */
export function useInstanceConfig({
    siloKey,
    structureKey,
    instanceKey,
    featuresSource,
    moduleFn,
}: {
    siloKey: string;
    structureKey: string;
    instanceKey: string;
    featuresSource?: FeaturesSource;
    moduleFn: () => Promise<{ structureModule: StructureModule }>;
}): InstanceConfig {
    const utils = useApiQueryUtils();
    const locale = useSiloLanguage();
    const [featuresQuery, moduleQuery] = useSuspenseQueries({
        queries: [
            featuresSource?.(utils, { siloKey, structureKey, instanceKey, locale }) ?? RESOLVED_EMPTY_FEATURES,
            structureModuleQueryOptions({ moduleFn, structureKey }),
        ],
    });
    const activeFeatures = featuresQuery.data;
    const { structureModule } = moduleQuery.data;

    return useMemo(
        () => ({
            siloKey,
            structureKey,
            instanceKey,
            structureModule,
            activeFeatures,
            ...assembleInstanceConfig({ structureModule, siloKey, structureKey, activeFeatures }),
        }),
        [structureModule, activeFeatures, siloKey, structureKey, instanceKey],
    );
}
